// Save/Load system with cloud save support and localStorage fallback
// Handles game state serialization and deserialization

import { GameState, createGameState } from './GameState';
import { GladiatorData } from '../entities/Gladiator';
import { TeamData } from '../entities/Team';
import { Race, RACES } from '../data/races';
import { findSpellById, Spell } from '../data/spells';
import { Item, findItemByName } from '../data/items';
import { apiUrl } from '../config';

const SAVE_KEY = 'openarena_save';
const SAVE_VERSION = 1;

// Save provider type - cloud or local
export type SaveProvider = 'cloud' | 'local';
let currentProvider: SaveProvider = 'local';

interface SaveData {
  version: number;
  timestamp: number;
  gameState: SerializedGameState;
}

interface SerializedGameState {
  month: number;
  year: number;
  playerTeamIds: string[];
  raceOfMonthId: number;

  teams: SerializedTeam[];
  gladiators: SerializedGladiator[];

  recruitableGladiatorIds: string[];
  currentRecruitRound: number;
  currentRecruitTeamIdx: number;
  recruitOrder: string[];
}

interface SerializedTeam {
  id: string;
  name: string;
  money: number;
  rating: number;
  arenaPoints: number;
  wins: number;
  losses: number;
  division: number;
  playerId: string | null;
  gladiatorIds: string[];
  battleGladiatorIds: (string | null)[];
  battleResults: number[];
  addedToBattle: boolean;
  currentMonthBalance: number;
  currentMonthIncome: number;
}

interface SerializedGladiator {
  id: string;
  name: string;
  raceId: number;
  age: number;
  salary: number;
  str: number;
  speed: number;
  hp: number;
  maxHp: number;
  mana: number;
  maxMana: number;
  strBonus: number;
  speedBonus: number;
  manaBonus: number;
  skills: {
    meleeAtk: [number, number, number, number, number];
    meleeDef: [number, number, number, number, number];
    rangedAtk: [number, number];
    abilities: [number, number];
    spellAtk: [number, number, number, number];
  };
  itemNames: [string | null, string | null, string | null];
  spellIds: [string | null, string | null, string | null, string | null];
  hurt: number;
  matches: number;
  takeOuts1: number;
  takeOuts2: number;
  ammo: number;
  needsReload: boolean;
  askPrice: number;
  offerPrice: number;
  offerTeamId: string | null;
  ownerId: string | null;
  isSpecial: boolean;
  trainingHistory?: {
    str: number;
    speed: number;
    maxHp: number;
    maxMana: number;
  };
}

// Cloud save metadata
interface CloudSaveMetadata {
  id: string;
  name: string;
  slotNumber: number;
  version: number;
  createdAt: number;
  updatedAt: number;
}

// Current cloud save ID (if loaded from cloud)
let currentCloudSaveId: string | null = null;

// Set the save provider
export function setSaveProvider(provider: SaveProvider): void {
  currentProvider = provider;
  console.log(`Save provider set to: ${provider}`);
}

// Get the current save provider
export function getSaveProvider(): SaveProvider {
  return currentProvider;
}

// Save game state (using current provider)
export async function saveGame(state: GameState, name?: string): Promise<boolean> {
  if (currentProvider === 'cloud') {
    return saveGameToCloud(state, name);
  }
  return saveGameToLocal(state);
}

// Load game state (using current provider)
export async function loadGame(saveId?: string): Promise<GameState | null> {
  if (currentProvider === 'cloud') {
    return loadGameFromCloud(saveId);
  }
  return loadGameFromLocal();
}

// Check if a save exists (using current provider)
export async function hasSaveGame(): Promise<boolean> {
  if (currentProvider === 'cloud') {
    return hasCloudSave();
  }
  return hasLocalSave();
}

// Delete save data (using current provider)
export async function deleteSave(saveId?: string): Promise<void> {
  if (currentProvider === 'cloud') {
    await deleteCloudSave(saveId);
  } else {
    deleteLocalSave();
  }
}

// List available cloud saves
export async function listCloudSaves(): Promise<CloudSaveMetadata[]> {
  try {
    const response = await fetch(apiUrl('/saves'));
    if (!response.ok) {
      throw new Error(`Failed to list saves: ${response.statusText}`);
    }
    const data = await response.json();
    return data.saves || [];
  } catch (error) {
    console.error('Failed to list cloud saves:', error);
    return [];
  }
}

// ============ Cloud Save Implementation ============

async function saveGameToCloud(state: GameState, name?: string): Promise<boolean> {
  try {
    const saveData: SaveData = {
      version: SAVE_VERSION,
      timestamp: Date.now(),
      gameState: serializeGameState(state),
    };

    const gameStateJson = JSON.stringify(saveData);
    const saveName = name || `Save ${new Date().toLocaleString('fi-FI')}`;

    if (currentCloudSaveId) {
      // Update existing save
      const response = await fetch(apiUrl(`/saves/${currentCloudSaveId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: saveName,
          gameState: gameStateJson,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update save: ${response.statusText}`);
      }
    } else {
      // Create new save
      const response = await fetch(apiUrl('/saves'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: saveName,
          slotNumber: 0,
          gameState: gameStateJson,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create save: ${response.statusText}`);
      }

      const data = await response.json();
      currentCloudSaveId = data.id;
    }

    console.log('Game saved to cloud successfully');

    // Also save to localStorage as backup
    saveGameToLocal(state);

    return true;
  } catch (error) {
    console.error('Failed to save to cloud, falling back to local:', error);
    return saveGameToLocal(state);
  }
}

async function loadGameFromCloud(saveId?: string): Promise<GameState | null> {
  try {
    const targetId = saveId || currentCloudSaveId;

    if (!targetId) {
      // No specific save, get the most recent one
      const saves = await listCloudSaves();
      if (saves.length === 0) {
        console.log('No cloud saves found');
        return null;
      }
      const mostRecent = saves[0];
      if (!mostRecent) {
        return null;
      }
      return loadGameFromCloud(mostRecent.id);
    }

    const response = await fetch(apiUrl(`/saves/${targetId}`));
    if (!response.ok) {
      throw new Error(`Failed to load save: ${response.statusText}`);
    }

    const data = await response.json();
    const saveData: SaveData = JSON.parse(data.save.gameState);

    if (saveData.version !== SAVE_VERSION) {
      console.warn(`Save version mismatch: ${saveData.version} vs ${SAVE_VERSION}`);
    }

    currentCloudSaveId = targetId;
    const state = deserializeGameState(saveData.gameState);
    console.log('Game loaded from cloud successfully');
    return state;
  } catch (error) {
    console.error('Failed to load from cloud, trying local:', error);
    return loadGameFromLocal();
  }
}

async function hasCloudSave(): Promise<boolean> {
  try {
    const saves = await listCloudSaves();
    return saves.length > 0;
  } catch {
    return false;
  }
}

async function deleteCloudSave(saveId?: string): Promise<void> {
  const targetId = saveId || currentCloudSaveId;
  if (!targetId) {
    console.log('No cloud save to delete');
    return;
  }

  try {
    const response = await fetch(apiUrl(`/saves/${targetId}`), {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete save: ${response.statusText}`);
    }

    if (targetId === currentCloudSaveId) {
      currentCloudSaveId = null;
    }

    console.log('Cloud save deleted');
  } catch (error) {
    console.error('Failed to delete cloud save:', error);
  }
}

// ============ Local Save Implementation ============

function saveGameToLocal(state: GameState): boolean {
  try {
    const saveData: SaveData = {
      version: SAVE_VERSION,
      timestamp: Date.now(),
      gameState: serializeGameState(state),
    };

    const json = JSON.stringify(saveData);
    localStorage.setItem(SAVE_KEY, json);

    console.log('Game saved to localStorage successfully');
    return true;
  } catch (error) {
    console.error('Failed to save game:', error);
    return false;
  }
}

function loadGameFromLocal(): GameState | null {
  try {
    const json = localStorage.getItem(SAVE_KEY);
    if (!json) {
      console.log('No local save data found');
      return null;
    }

    const saveData: SaveData = JSON.parse(json);

    if (saveData.version !== SAVE_VERSION) {
      console.warn(`Save version mismatch: ${saveData.version} vs ${SAVE_VERSION}`);
    }

    const state = deserializeGameState(saveData.gameState);
    console.log('Game loaded from localStorage successfully');
    return state;
  } catch (error) {
    console.error('Failed to load game:', error);
    return null;
  }
}

function hasLocalSave(): boolean {
  return localStorage.getItem(SAVE_KEY) !== null;
}

function deleteLocalSave(): void {
  localStorage.removeItem(SAVE_KEY);
  console.log('Local save deleted');
}

// ============ Serialization ============

function serializeGameState(state: GameState): SerializedGameState {
  const teams: SerializedTeam[] = [];
  for (const [, team] of state.teams) {
    teams.push(serializeTeam(team));
  }

  const gladiators: SerializedGladiator[] = [];
  for (const [, gladiator] of state.gladiators) {
    gladiators.push(serializeGladiator(gladiator));
  }

  return {
    month: state.month,
    year: state.year,
    playerTeamIds: state.playerTeamIds,
    raceOfMonthId: state.raceOfMonth.id,
    teams,
    gladiators,
    recruitableGladiatorIds: state.recruitableGladiatorIds,
    currentRecruitRound: state.currentRecruitRound,
    currentRecruitTeamIdx: state.currentRecruitTeamIdx,
    recruitOrder: state.recruitOrder,
  };
}

function serializeTeam(team: TeamData): SerializedTeam {
  return {
    id: team.id,
    name: team.name,
    money: team.money,
    rating: team.rating,
    arenaPoints: team.arenaPoints,
    wins: team.wins,
    losses: team.losses,
    division: team.division,
    playerId: team.playerId,
    gladiatorIds: [...team.gladiatorIds],
    battleGladiatorIds: [...team.battleGladiatorIds],
    battleResults: [...team.battleResults],
    addedToBattle: team.addedToBattle,
    currentMonthBalance: team.currentMonthBalance,
    currentMonthIncome: team.currentMonthIncome,
  };
}

function serializeGladiator(gladiator: GladiatorData): SerializedGladiator {
  return {
    id: gladiator.id,
    name: gladiator.name,
    raceId: gladiator.race.id,
    age: gladiator.age,
    salary: gladiator.salary,
    str: gladiator.str,
    speed: gladiator.speed,
    hp: gladiator.hp,
    maxHp: gladiator.maxHp,
    mana: gladiator.mana,
    maxMana: gladiator.maxMana,
    strBonus: gladiator.strBonus,
    speedBonus: gladiator.speedBonus,
    manaBonus: gladiator.manaBonus,
    skills: {
      meleeAtk: [...gladiator.skills.meleeAtk],
      meleeDef: [...gladiator.skills.meleeDef],
      rangedAtk: [...gladiator.skills.rangedAtk],
      abilities: [...gladiator.skills.abilities],
      spellAtk: [...gladiator.skills.spellAtk],
    },
    itemNames: [
      gladiator.items[0]?.name ?? null,
      gladiator.items[1]?.name ?? null,
      gladiator.items[2]?.name ?? null,
    ],
    spellIds: [
      gladiator.knownSpells[0]?.id ?? null,
      gladiator.knownSpells[1]?.id ?? null,
      gladiator.knownSpells[2]?.id ?? null,
      gladiator.knownSpells[3]?.id ?? null,
    ],
    hurt: gladiator.hurt,
    matches: gladiator.matches,
    takeOuts1: gladiator.takeOuts1,
    takeOuts2: gladiator.takeOuts2,
    ammo: gladiator.ammo,
    needsReload: gladiator.needsReload,
    askPrice: gladiator.askPrice,
    offerPrice: gladiator.offerPrice,
    offerTeamId: gladiator.offerTeamId,
    ownerId: gladiator.ownerId,
    isSpecial: gladiator.isSpecial,
    trainingHistory: gladiator.trainingHistory ?? { str: 0, speed: 0, maxHp: 0, maxMana: 0 },
  };
}

// ============ Deserialization ============

function deserializeGameState(data: SerializedGameState): GameState {
  const state = createGameState();

  state.month = data.month;
  state.year = data.year;
  state.playerTeamIds = data.playerTeamIds;
  state.raceOfMonth = RACES.find(r => r.id === data.raceOfMonthId) ?? RACES[0];
  state.recruitableGladiatorIds = data.recruitableGladiatorIds;
  state.currentRecruitRound = data.currentRecruitRound;
  state.currentRecruitTeamIdx = data.currentRecruitTeamIdx;
  state.recruitOrder = data.recruitOrder;

  // Deserialize gladiators first
  for (const serialized of data.gladiators) {
    const gladiator = deserializeGladiator(serialized);
    state.gladiators.set(gladiator.id, gladiator);
  }

  // Then deserialize teams
  for (const serialized of data.teams) {
    const team = deserializeTeam(serialized);
    state.teams.set(team.id, team);
  }

  return state;
}

function deserializeTeam(data: SerializedTeam): TeamData {
  return {
    id: data.id,
    name: data.name,
    money: data.money,
    rating: data.rating,
    arenaPoints: data.arenaPoints,
    wins: data.wins,
    losses: data.losses,
    division: data.division,
    playerId: data.playerId,
    gladiatorIds: [...data.gladiatorIds],
    battleGladiatorIds: [...data.battleGladiatorIds],
    battleResults: [...data.battleResults],
    addedToBattle: data.addedToBattle,
    currentMonthBalance: data.currentMonthBalance,
    currentMonthIncome: data.currentMonthIncome,
  };
}

function deserializeGladiator(data: SerializedGladiator): GladiatorData {
  const race: Race = RACES.find(r => r.id === data.raceId) ?? RACES[0];

  // Reconstruct items
  const items: [Item | null, Item | null, Item | null] = [
    data.itemNames[0] ? findItemByName(data.itemNames[0]) ?? null : null,
    data.itemNames[1] ? findItemByName(data.itemNames[1]) ?? null : null,
    data.itemNames[2] ? findItemByName(data.itemNames[2]) ?? null : null,
  ];

  // Reconstruct spells
  const knownSpells: [Spell | null, Spell | null, Spell | null, Spell | null] = [
    data.spellIds[0] ? findSpellById(data.spellIds[0]) ?? null : null,
    data.spellIds[1] ? findSpellById(data.spellIds[1]) ?? null : null,
    data.spellIds[2] ? findSpellById(data.spellIds[2]) ?? null : null,
    data.spellIds[3] ? findSpellById(data.spellIds[3]) ?? null : null,
  ];

  return {
    id: data.id,
    name: data.name,
    race,
    age: data.age,
    salary: data.salary,
    str: data.str,
    speed: data.speed,
    hp: data.hp,
    maxHp: data.maxHp,
    mana: data.mana,
    maxMana: data.maxMana,
    strBonus: data.strBonus,
    speedBonus: data.speedBonus,
    manaBonus: data.manaBonus,
    skills: {
      meleeAtk: [...data.skills.meleeAtk],
      meleeDef: [...data.skills.meleeDef],
      rangedAtk: [...data.skills.rangedAtk],
      abilities: [...data.skills.abilities],
      spellAtk: [...data.skills.spellAtk],
    },
    items,
    knownSpells,
    hurt: data.hurt,
    matches: data.matches,
    takeOuts1: data.takeOuts1,
    takeOuts2: data.takeOuts2,
    position: null,
    receivedDmg: -1,
    ammo: data.ammo,
    needsReload: data.needsReload,
    hasGainedLevel: false,
    skillUpgrades: {
      meleeAtk: [0, 0, 0, 0, 0],
      meleeDef: [0, 0, 0, 0, 0],
      rangedAtk: [0, 0],
      abilities: [0, 0],
      spellAtk: [0, 0, 0, 0],
    },
    activeEffects: [],
    stunTurns: 0,
    rootedTurns: 0,
    askPrice: data.askPrice,
    offerPrice: data.offerPrice,
    offerTeamId: data.offerTeamId,
    ownerId: data.ownerId,
    isSpecial: data.isSpecial,
    trainingHistory: data.trainingHistory ?? { str: 0, speed: 0, maxHp: 0, maxMana: 0 },
  };
}
